import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../Modal';
import * as X from '../../lib/linksExtras';
import './LinksExtras.css';

/**
 * The Links page's extras (Gordon, 22 October; migration 076):
 *
 *   FAVOURITES - a star on every link; the starred ones on a page of their
 *   own at the top of the list, in each person's order (drag, or arrow keys
 *   on the handle). Everybody has their own, a contractor too.
 *
 *   MOST USED AT NCMB - what the firm opens, ranked by how many people over
 *   the last thirty days. Never who. Staff see the numbers; nobody else does.
 *
 *   TODAY'S RATES - the ECB's euro reference rates for the currencies a
 *   person picks, a tiny arrow for the change on the day before, and a
 *   converter between any two (through the euro, as the ECB publishes them).
 *
 * Everything here waits on the server: before 076 the extras answer 503 and
 * the page is exactly what it was - no star, no card, no Favourites.
 */

/** The page slug Favourites answers to. A real page's slug has no underscore. */
export const FAV_SLUG = '_favourites';
const OPEN_KEY = 'jarvis-links-rates-open';

/** The ECB's currencies, by the names its own page gives them. */
export const CURRENCY_NAMES = {
  EUR: 'Euro', USD: 'US dollar', JPY: 'Japanese yen', CZK: 'Czech koruna', DKK: 'Danish krone',
  GBP: 'Pound sterling', HUF: 'Hungarian forint', PLN: 'Polish zloty', RON: 'Romanian leu', SEK: 'Swedish krona',
  CHF: 'Swiss franc', ISK: 'Icelandic krona', NOK: 'Norwegian krone', TRY: 'Turkish lira', AUD: 'Australian dollar',
  BRL: 'Brazilian real', CAD: 'Canadian dollar', CNY: 'Chinese yuan renminbi', HKD: 'Hong Kong dollar',
  IDR: 'Indonesian rupiah', ILS: 'Israeli shekel', INR: 'Indian rupee', KRW: 'South Korean won', MXN: 'Mexican peso',
  MYR: 'Malaysian ringgit', NZD: 'New Zealand dollar', PHP: 'Philippine peso', SGD: 'Singapore dollar',
  THB: 'Thai baht', ZAR: 'South African rand', BGN: 'Bulgarian lev'
};
const nameOf = (c) => CURRENCY_NAMES[c] || c;

/* ================================================================ state */

export const ExtrasContext = React.createContext(null);

/**
 * The extras for this person: { status, favourites, mostUsed, rates, ... }.
 * `say(message, { undo, error })` is the page's toast.
 */
export function useLinksExtras(say) {
  const [extras, setExtras] = useState({ status: 'loading', favourites: [], mostUsed: [], days: 30 });
  const [rates, setRates] = useState(null);
  const sayRef = useRef(say);
  sayRef.current = say;
  const recent = useRef(new Map());

  const loadExtras = useCallback(async () => {
    try {
      const d = await X.loadExtras();
      setExtras({ status: 'ready', favourites: d.favourites || [], mostUsed: d.most_used || [], days: d.most_used_days || 30 });
    } catch {
      // Before 076 (a 503), or anything else: the page goes without.
      setExtras((e) => ({ ...e, status: e.status === 'ready' ? 'ready' : 'off' }));
    }
  }, []);

  useEffect(() => {
    let live = true;
    loadExtras();
    X.loadRates().then((d) => { if (live && d?.date) setRates(d); }).catch(() => {});
    return () => { live = false; };
  }, [loadExtras]);

  const favSet = useMemo(() => new Set(extras.favourites), [extras.favourites]);

  const setFavs = (fn) => setExtras((e) => ({ ...e, favourites: typeof fn === 'function' ? fn(e.favourites) : fn }));

  const toggle = useCallback(async (link) => {
    const was = extras.favourites;
    const on = was.includes(link.id);
    const index = was.indexOf(link.id);
    setFavs(on ? was.filter((x) => x !== link.id) : [...was, link.id]);
    try {
      const ids = on ? await X.unstar(link.id) : await X.star(link.id);
      setFavs(ids);
      if (on) {
        sayRef.current?.(`Removed “${link.title}” from favourites`, {
          undo: async () => {
            try { setFavs(await X.star(link.id, index)); sayRef.current?.(`“${link.title}” is back in favourites`); } catch (e) {
              sayRef.current?.(e?.response?.data?.error || 'Could not put it back', { error: true });
            }
          }
        });
      } else {
        sayRef.current?.(`Added “${link.title}” to favourites`);
      }
    } catch (e) {
      setFavs(was);
      sayRef.current?.(e?.response?.data?.error || (on ? 'Could not unstar that' : 'Could not star that'), { error: true });
    }
  }, [extras.favourites]);

  /** Move favourite `from` to `to` (indexes among the favourites shown). */
  const move = useCallback(async (shownIds, from, to) => {
    const id = shownIds[from];
    if (!id || from === to) return;
    const next = [...shownIds];
    next.splice(from, 1);
    next.splice(to, 0, id);
    const was = extras.favourites;
    // Hidden favourites (a Windows app seen from a Mac) keep their places after the shown ones.
    setFavs([...next, ...was.filter((x) => !next.includes(x))]);
    try {
      // The server's index is among all my live favourites; the shown ones
      // come first in that order unless something is hidden between them,
      // so ask for the place of the one it now follows.
      const target = to === 0 ? 0 : was.filter((x) => x !== id).indexOf(next[to - 1]) + 1;
      setFavs(await X.moveFavourite(id, target));
    } catch (e) {
      setFavs(was);
      sayRef.current?.(e?.response?.data?.error || 'Could not move that favourite', { error: true });
    }
  }, [extras.favourites]);

  /** An open, counted at most once a few seconds here (the server keeps ten minutes). */
  const record = useCallback((id) => {
    if (!id || extras.status !== 'ready') return;
    const now = Date.now();
    if (now - (recent.current.get(id) || 0) < 5000) return;
    recent.current.set(id, now);
    X.recordOpen(id);
  }, [extras.status]);

  const pin = useCallback(async (list) => {
    const was = rates;
    setRates((r) => (r ? { ...r, pinned: list || r.defaults, custom: Boolean(list) } : r));
    try {
      const d = await X.saveCurrencies(list);
      setRates((r) => (r ? { ...r, pinned: d.pinned, custom: d.custom } : r));
    } catch (e) {
      setRates(was);
      sayRef.current?.(e?.response?.data?.error || 'Could not save your currencies', { error: true });
    }
  }, [rates]);

  const ctx = useMemo(() => ({
    ready: extras.status === 'ready',
    isFav: (id) => favSet.has(id),
    toggle,
    record
  }), [extras.status, favSet, toggle, record]);

  return { ...extras, rates, ctx, toggle, move, record, pin, reload: loadExtras };
}

/* ================================================================ icons */

export function StarGlyph({ filled = false, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
         fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M12 2.8l2.84 5.75 6.35.92-4.6 4.48 1.09 6.32L12 17.29l-5.68 2.98 1.09-6.32-4.6-4.48 6.35-.92z" />
    </svg>
  );
}

const Glyph = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {[].concat(d).map((p) => <path key={p} d={p} />)}
  </svg>
);
const SWAP = ['M7 16V4', 'M3 8l4-4 4 4', 'M17 8v12', 'M21 16l-4 4-4-4'];
const COPY = ['M9 9h11v11H9z', 'M5 15H4V4h11v1'];
const CHEVRON = ['M6 9l6 6 6-6'];
const SLIDERS = ['M4 21v-7', 'M4 10V3', 'M12 21v-9', 'M12 8V3', 'M20 21v-5', 'M20 12V3', 'M1 14h6', 'M9 8h6', 'M17 16h6'];
const EXTERNAL = ['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6', 'M15 3h6v6', 'M10 14L21 3'];

/* ================================================================= star */

/** The star on a tile: a toggle button, beside the link rather than in it. */
export function StarButton({ link }) {
  const ex = useContext(ExtrasContext);
  if (!ex?.ready || !link?.id) return null;
  const on = ex.isFav(link.id);
  return (
    <button type="button" className={`lk-star${on ? ' is-on' : ''}`} aria-pressed={on}
            aria-label={`Favourite: ${link.title}`} title={on ? 'Remove from favourites' : 'Add to favourites'}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); ex.toggle(link); }}>
      <StarGlyph filled={on} size={15} />
    </button>
  );
}

/* =========================================================== favourites */

/** Every live link on the shelf by id, with where it lives. */
export function indexLinks(pages) {
  const m = new Map();
  for (const p of pages || []) {
    for (const s of p.sections || []) for (const l of s.links || []) m.set(l.id, { link: l, page: p, section: s });
  }
  return m;
}

/**
 * The Favourites page: mine, in my order, then the firm's most used.
 * `renderTile(link, where, name)` and `renderHandle(link, index, total)` are the
 * shelf's own, so a favourite looks and opens exactly as it does on its page.
 */
export function FavouritesView({ favourites, mostUsed, status, days, renderTile, renderHandle, markOf, showCounts }) {
  const n = favourites.length;
  return (
    <>
      <div className="lk-main-head">
        <h2 className="lk-main-title">
          <span className="lk-main-icon lk-fav-icon"><StarGlyph filled size={19} /></span>
          Favourites
        </h2>
      </div>

      <section className="lk-card lk-favs" aria-labelledby="lk-favs-title">
        <header className="lk-card-head">
          <h3 className="lk-card-title" id="lk-favs-title">Your favourites</h3>
          {status === 'ready' && <span className="lk-count" aria-label={`${n} favourite${n === 1 ? '' : 's'}`}>{n}</span>}
        </header>
        {status === 'loading' && (
          <div className="lk-fav-list" aria-busy="true">{[0, 1, 2].map((i) => <span key={i} className="lk-skel lk-skel-row" />)}</div>
        )}
        {status === 'ready' && n === 0 && (
          <div className="lk-favs-empty">
            <span className="lk-favs-empty-star"><StarGlyph size={22} /></span>
            <p><strong>Star the links you use most</strong> and they wait for you here, in the order you like.</p>
            <p className="lk-muted">Point at a link and press its star{' '}
              <span className="lk-inline-star"><StarGlyph size={13} /></span> - on a phone the star is always there.</p>
          </div>
        )}
        {status === 'ready' && n > 0 && (
          <ul className="lk-fav-list" data-lk-order="">
            {favourites.map((f, i) => (
              <li key={f.link.id} data-lk-item="" className={`lk-fav-item${markOf('favourites', i, n)}`}>
                {renderHandle(f.link, i, n)}
                <ul className="lk-tiles">{renderTile(f.link, f.where, f.name)}</ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="lk-card lk-used" aria-labelledby="lk-used-title">
        <header className="lk-card-head">
          <h3 className="lk-card-title" id="lk-used-title">Most used at NCMB</h3>
          <span className="lk-used-sub">last {days} days</span>
        </header>
        {status === 'ready' && mostUsed.length === 0 && (
          <p className="lk-empty-line">Nothing yet - the links the firm opens most will show here.</p>
        )}
        {mostUsed.length > 0 && (
          <ol className="lk-used-list">
            {mostUsed.map((u, i) => (
              <li key={u.link.id} className="lk-used-item">
                <span className="lk-used-rank" aria-hidden="true">{i + 1}</span>
                <ul className="lk-tiles">{renderTile(u.link, u.where, u.name)}</ul>
                {showCounts && Number.isInteger(u.people) && (
                  <span className="lk-used-n" title={`${u.people} ${u.people === 1 ? 'person' : 'people'} on any day, ${u.opens} opens, in ${days} days - only staff see this`}>
                    {u.people}<span className="lk-sr"> people, {u.opens} opens</span>
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
        <p className="lk-used-note">Counted across the firm, never who opened what.</p>
      </section>
    </>
  );
}

/* ================================================================ rates */

const minorOf = (code) => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits; } catch { return 2; }
};
const fmtRate = (v) => (v == null ? '-' : v.toLocaleString('en-GB', { minimumFractionDigits: v >= 100 ? 2 : 4, maximumFractionDigits: v >= 100 ? 2 : 5 }));
const fmtUnit = (v) => (v == null || !Number.isFinite(v) ? '-'
  : v < 0.01 ? v.toLocaleString('en-GB', { maximumSignificantDigits: 4 }) : fmtRate(v));
const fmtMoney = (v, code) => v.toLocaleString('en-GB', { minimumFractionDigits: minorOf(code), maximumFractionDigits: minorOf(code) });
export const fmtDay = (d, opts = {}) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB',
  { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC', ...opts }) : '');

/** "1,000.50", "1 000", "250" - the amount typed, or NaN. */
export const parseAmount = (s) => {
  const t = String(s ?? '').replace(/[\s,'_]/g, '');
  if (!/^\d*\.?\d+$|^\d+\.$/.test(t)) return NaN;
  return Number(t);
};

function useNarrow(query = '(max-width: 760px)') {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return narrow;
}

function Change({ rate, prev, prevDate }) {
  if (prev == null || !prev) return <span className="lk-fx-chg is-none" aria-hidden="true" />;
  const pct = ((rate - prev) / prev) * 100;
  if (Math.abs(pct) < 0.005) {
    return <span className="lk-fx-chg is-flat" title={`Unchanged on ${fmtDay(prevDate, { year: undefined })}`}><span className="lk-sr">unchanged</span></span>;
  }
  const up = pct > 0;
  const words = `${up ? 'Up' : 'Down'} ${Math.abs(pct).toFixed(2)}% on ${fmtDay(prevDate, { year: undefined })} (${fmtRate(prev)})`;
  return (
    <span className={`lk-fx-chg ${up ? 'is-up' : 'is-down'}`} title={words}>
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" focusable="false">
        <path d={up ? 'M4 1l3.5 5.5h-7z' : 'M4 7L.5 1.5h7z'} fill="currentColor" />
      </svg>
      <span className="lk-sr">{words}</span>
    </span>
  );
}

function CurrencyPicker({ data, onPin, onClose }) {
  const [list, setList] = useState(data.pinned);
  const all = useMemo(() => [...data.rates].sort((a, b) => nameOf(a.currency).localeCompare(nameOf(b.currency))), [data.rates]);
  const full = list.length >= 16;
  const flip = (c) => setList((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]));
  const same = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
  return (
    <Modal title="Your currencies" subtitle="Which rates the card shows, in the order you tick them" onClose={onClose}
           footer={(
             <>
               <button type="button" className="btn btn-plain lk-fx-reset" disabled={same(list, data.defaults)}
                       onClick={() => setList(data.defaults.filter((c) => data.rates.some((r) => r.currency === c)))}>Use the defaults</button>
               <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
               <button type="button" className="btn btn-primary" disabled={!list.length}
                       onClick={() => { onPin(same(list, data.defaults) ? null : list); onClose(); }}>Save</button>
             </>
           )}>
      <fieldset className="lk-fx-pick">
        <legend className="lk-sr">Currencies</legend>
        {all.map((r) => {
          const on = list.includes(r.currency);
          return (
            <label key={r.currency} className={`lk-fx-pick-row${on ? ' is-on' : ''}`}>
              <input type="checkbox" checked={on} disabled={!on && full} onChange={() => flip(r.currency)} />
              <span className="lk-fx-code">{r.currency}</span>
              <span className="lk-fx-name">{nameOf(r.currency)}</span>
              {on && <span className="lk-fx-order" aria-label={`shown ${list.indexOf(r.currency) + 1}`}>{list.indexOf(r.currency) + 1}</span>}
              <span className="lk-fx-rate">{fmtRate(r.rate)}</span>
            </label>
          );
        })}
      </fieldset>
      {full && <p className="lk-muted lk-fx-full">Sixteen at most - untick one to add another.</p>}
    </Modal>
  );
}

/**
 * The rates card: my currencies against the euro, the change on the day
 * before, a converter, and where it all comes from. On a phone it folds to
 * one line so the links stay near the top; opening it is remembered.
 */
export function RatesCard({ data, onPin, say }) {
  const narrow = useNarrow();
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; } });
  const [picker, setPicker] = useState(false);
  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('EUR');
  const [to, setTo] = useState(() => data.pinned.find((c) => c !== 'EUR') || 'USD');
  const byCode = useMemo(() => new Map(data.rates.map((r) => [r.currency, r])), [data.rates]);
  const shown = data.pinned.map((c) => byCode.get(c)).filter(Boolean);
  const codes = useMemo(() => ['EUR', ...[...byCode.keys()].sort()], [byCode]);
  const rateOf = (c) => (c === 'EUR' ? 1 : byCode.get(c)?.rate);

  const value = parseAmount(amount);
  const result = Number.isFinite(value) && rateOf(from) && rateOf(to) ? (value * rateOf(to)) / rateOf(from) : null;
  const unit = rateOf(from) && rateOf(to) ? rateOf(to) / rateOf(from) : null;
  const folded = narrow && !open;
  const toggle = () => setOpen((v) => { try { localStorage.setItem(OPEN_KEY, v ? '0' : '1'); } catch { /* private window */ } return !v; });

  const copy = async () => {
    if (result == null) return;
    const text = result.toFixed(minorOf(to));
    try { await navigator.clipboard.writeText(text); say(`Copied ${text}`); } catch { say('Could not copy - your browser blocked it', { error: true }); }
  };

  const title = (
    <>
      <span className="lk-fx-euro" aria-hidden="true">€</span>
      <span className="lk-fx-title-text">Today’s rates</span>
    </>
  );
  return (
    <section className={`lk-fx${folded ? ' is-folded' : ''}`} aria-labelledby="lk-fx-title">
      <header className="lk-fx-head">
        {narrow ? (
          <button type="button" className="lk-fx-toggle" id="lk-fx-title" aria-expanded={!folded} aria-controls="lk-fx-body" onClick={toggle}>
            {title}
            {folded && (
              <span className="lk-fx-peek" aria-hidden="true">
                {shown.slice(0, 3).map((r) => <span key={r.currency}>{r.currency} {fmtRate(r.rate)}</span>)}
              </span>
            )}
            <span className={`lk-fx-chev${folded ? '' : ' is-open'}`}><Glyph d={CHEVRON} size={16} /></span>
          </button>
        ) : (
          <h2 className="lk-fx-title" id="lk-fx-title">{title}</h2>
        )}
        {!folded && (
          <button type="button" className="lk-fx-icon" onClick={() => setPicker(true)} aria-label="Choose currencies" title="Choose currencies">
            <Glyph d={SLIDERS} size={15} />
          </button>
        )}
      </header>

      {!folded && (
        <div className="lk-fx-body" id="lk-fx-body">
          <p className="lk-fx-base">€1 buys</p>
          <ul className="lk-fx-list">
            {shown.map((r) => (
              <li key={r.currency} className="lk-fx-row">
                <button type="button" className="lk-fx-pickrow" onClick={() => { setFrom('EUR'); setTo(r.currency); }}
                        title={`Convert euro to ${nameOf(r.currency)}`}>
                  <span className="lk-fx-code">{r.currency}</span>
                  <span className="lk-fx-name">{nameOf(r.currency)}</span>
                  <span className="lk-fx-rate">{fmtRate(r.rate)}</span>
                  <Change rate={r.rate} prev={r.prev} prevDate={data.prev_date} />
                </button>
              </li>
            ))}
          </ul>

          <div className="lk-fx-conv" role="group" aria-label="Currency converter">
            <label className="lk-fx-amount">
              <span className="lk-sr">Amount</span>
              <input type="text" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => setAmount(e.target.value)}
                     aria-invalid={amount !== '' && !Number.isFinite(value)} />
            </label>
            <div className="lk-fx-pair">
              <label className="lk-fx-sel">
                <span className="lk-sr">From</span>
                <select value={from} onChange={(e) => setFrom(e.target.value)}>
                  {codes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <button type="button" className="lk-fx-icon lk-fx-swap" aria-label="Swap the two currencies" title="Swap"
                      onClick={() => { setFrom(to); setTo(from); }}>
                <Glyph d={SWAP} size={15} />
              </button>
              <label className="lk-fx-sel">
                <span className="lk-sr">To</span>
                <select value={to} onChange={(e) => setTo(e.target.value)}>
                  {codes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <div className="lk-fx-result" aria-live="polite">
              <output className="lk-fx-out">
                {result == null ? <span className="lk-muted">-</span> : <><strong>{fmtMoney(result, to)}</strong> <span>{to}</span></>}
              </output>
              <button type="button" className="lk-fx-icon" onClick={copy} disabled={result == null}
                      aria-label="Copy the result" title="Copy the result">
                <Glyph d={COPY} size={15} />
              </button>
            </div>
            {unit != null && from !== to && (
              <p className="lk-fx-unit">1 {from} = {fmtUnit(unit)} {to}</p>
            )}
          </div>

          {data.problem && <p className="lk-fx-problem" role="status">{data.problem} - showing {fmtDay(data.date)}.</p>}
          <p className="lk-fx-source">
            <a href={data.source?.url} target="_blank" rel="noopener noreferrer">
              ECB reference rate, {fmtDay(data.date)},{' '}
              <span className="lk-fx-nowrap">{data.source?.published || '16:00 CET'}<Glyph d={EXTERNAL} size={11} /></span>
            </a>
          </p>
          <p className="lk-fx-note">For information - not the rate a bank will give.</p>
        </div>
      )}
      {/* Out of the sticky side column, whose stacking would put the dialog under the page. */}
      {picker && createPortal(<CurrencyPicker data={data} onPin={onPin} onClose={() => setPicker(false)} />, document.body)}
    </section>
  );
}

/** In search: "usd" or "€ gbp" shows that rate above the results. */
export function RatesHit({ query, data }) {
  if (!data) return null;
  const code = String(query || '').trim().replace(/^€\s*|^eur\s*(to|>|→)?\s*/i, '').toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return null;
  const r = data.rates.find((x) => x.currency === code);
  if (!r) return null;
  return (
    <p className="lk-fx-hit" role="status">
      <span className="lk-fx-euro" aria-hidden="true">€</span>
      <span>€1 = <strong>{fmtRate(r.rate)} {r.currency}</strong> <span className="lk-muted">({nameOf(r.currency)}) · 1 {r.currency} = {fmtUnit(1 / r.rate)} EUR · ECB, {fmtDay(data.date)}</span></span>
    </p>
  );
}
